import type { DepositLookupResponse } from '@massilia/queries'

import { useAppForm } from '@massilia/features'
import { useLookupBookingForDepositMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Search } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { depositLookupSchema } from '@/lib/schemas'

interface DepositLookupFormProps {
  initialValues?: {
    dossierNumber?: string
    email?: string
    lastName?: string
  }
  /** Moyen de règlement visé : ne sert qu'à annoncer le bon titre avant la recherche. */
  mode?: 'installments'
  onBookingFound: (
    booking: DepositLookupResponse,
    credentials: { dossierNumber: string; email: string; lastName: string }
  ) => void
}

export function DepositLookupForm({ initialValues, mode, onBookingFound }: DepositLookupFormProps) {
  const navigate = useNavigate()
  const { mutateAsync: lookupBooking } = useLookupBookingForDepositMutation()
  const autoSubmittedRef = useRef(false)

  const shouldAutoSubmit =
    !!initialValues?.dossierNumber && !!initialValues?.email && !!initialValues?.lastName

  const form = useAppForm({
    defaultValues: {
      email: initialValues?.email ?? '',
      lastName: initialValues?.lastName ?? '',
      dossierNumber: initialValues?.dossierNumber ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        const booking = await lookupBooking(value)
        onBookingFound(booking, value)
      } catch {
        await navigate({
          search: {
            email: value.email,
            lastName: value.lastName,
            dossierNumber: value.dossierNumber,
          },
          to: '/payer-acompte/ancien',
        })
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, depositLookupSchema),
    },
  })

  useEffect(() => {
    if (shouldAutoSubmit && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true
      void form.handleSubmit()
    }
  }, [shouldAutoSubmit, form])

  return (
    <div className="mx-auto w-full max-w-lg">
      <div className="mb-6 text-center">
        <h1 className="text-gray-light-900 text-2xl font-semibold">
          {mode === 'installments' ? 'Payer mon solde en 3/4x' : 'Payer un acompte en CB'}
        </h1>
        <p className="text-gray-light-600 mt-2 text-sm">
          Renseignez les informations de votre dossier
          <br />
          pour effectuer un paiement.
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField
          name="dossierNumber"
          children={(field) => <field.TextField label="Numéro de dossier" />}
        />
        <form.AppField
          name="email"
          children={(field) => (
            <field.TextField label="Adresse email utilisée lors de la réservation" />
          )}
        />
        <form.AppField
          name="lastName"
          children={(field) => <field.TextField label="Nom de famille (d'un des passagers)" />}
        />

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              className="bg-brand-600 hover:bg-brand-700 mt-2 w-full px-4 py-3 text-base font-medium text-white"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <>
                  <Search className="mr-2 size-4" />
                  Rechercher mon dossier
                </>
              )}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </div>
  )
}
